import { OutlineElement } from '../../base/outline-element/outline-element';
import { CSSResultGroup, html, nothing, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import componentStyles from './ghs-hero-banner.css.lit';
import '../ghs-icon/ghs-icon';
import { LegacySlotController } from '../../controllers/legacy-slot-controller';

import '../../base/outline-container/outline-container';

import { BackgroundVariant, HorizontalAlignmentVariant } from '../types';

import {
  screenSizeMap,
  ScreenSizeController,
} from '../../controllers/screen-size-controller';

/**
 *
 * @elementsList - this is a list of all elements and attributes, surveyed from Figma.
 * -background - always present, sometimes image with gradient, sometimes color with gradient. Different images for mobile an desktop? Implement color, then color with gradient, to start. Background image may be trickly. (source for correctly cropped images?)
 * -buttons - optional, 0-2, always left aligned
 * -body text - optional, one paragraph, always left-aligned
 * -title - always present, first letters _NOT_ always capitalized, always left-aligned.
 * -eyebrow - optional, always dark <hr /> underneath, always left-aligned
 * -breadcrumb - optional, different for mobile and desktop, always left-aligned
 * -hero image - optional, right-aligned (except mobile), sometimes in the middle
 * -date - optional, w/ icon, always left-aligned
 * -location - optional, w/ icon, always left aligned.
 * -phone - optional, w/ icon, always bottom, always left aligned.
 * -author - optional, always with division, always left aligned.
 * -social icons - optional, always left aligned
 *
 * @orderedElementsList - this is a list of all elements, with their order pieced together, from Figma.
 * background
 *    breadcrumb
 *    hero image
 *    body
 *        eyebrow
 *        title
 *        author
 *        date
 *        location
 *        body text
 *        buttons
 *        phone number
 *        social icons
 *
 */

/**
 * The GHS Hero Banner component
 * @element ghs-hero-banner
 * @slot - default
 * @slot hero-banner-buttons - for buttons
 * @slot hero-banner-breadcrumb - for breadcrumb
 * @slot hero-banner-details - for provider details
 */
@customElement('ghs-hero-banner')
export class GhsHeroBanner extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  // This uses the legacy slot controller because the newer slot controller breaks the facet select filters on views.
  legacySlotController = new LegacySlotController(this);

  // eslint-disable-next-line
  // @ts-ignore
  private _screenSizeController = new ScreenSizeController(this);

  /**
   * Changes the background of the component
   * @param backgroundVariant
   * @default 'blue-gradient'
   */
  @property({ type: String, attribute: 'background-variant', reflect: true })
  backgroundVariant: BackgroundVariant = 'green-gradient';

  /**
   * Adds padding if only the title is showing.
   * @param onlyTitle
   * @default false
   */
  @property({ type: Boolean, attribute: 'only-title', reflect: true })
  onlyTitle: false;

  /**
   * Adds a background-image to the component
   * @param backgroundImageUrl
   * @default ''
   */
  @property({ type: String, attribute: 'background-image-url', reflect: true })
  backgroundImageUrl: string;

  /**
   * Adds a Connect CTA class to component for customization.
   */
  @property({ type: Boolean, attribute: 'connect-cta', reflect: true })
  connectCTA = false;

  /**
   * Makes the background image full width.
   */
  @property({ type: Boolean, attribute: 'full-width', reflect: true })
  fullWidth = false;

  /**
   * Hides the image on mobile if true
   */
  @property({ type: Boolean, attribute: 'hide-mobile-image', reflect: true })
  hideMobileImage = false;

  /**
   * The Banner content's alignment relative to the image.
   * @param alignment
   * @default 'left-align'
   */
  @property({ type: String, reflect: true })
  alignment: HorizontalAlignmentVariant = 'right-align';

  /**
   * Use a full bleed container for the form.
   */
  @property({ type: Boolean, attribute: 'form-full-bleed', reflect: true })
  formFullBleed = false;

  /**
   * Use a full bleed extend container to override the current BH contraint.
   */
  @property({ type: Boolean, attribute: 'full-bleed-extend', reflect: true })
  fullBleedExtend = false;

  /**
   * Adjusts styling if alternate set to true
   */
  @property({ type: Boolean, attribute: 'alternate', reflect: true })
  alternate = false;

  @state() hasBreadcrumbSlot: boolean;
  @state() hasEyebrowSlot: boolean;
  @state() hasTitleSlot: boolean;
  @state() hasAuthorSlot: boolean;
  @state() hasDateSlot: boolean;
  @state() hasLocationSlot: boolean;
  @state() hasBodyTextSlot: boolean;
  @state() hasButtonsSlot: boolean;
  @state() hasPhoneSlot: boolean;
  @state() hasSocialSlot: boolean;
  @state() hasImageSlot: boolean;
  @state() hasDetailsSlot: boolean;
  @state() hasFormSlot: boolean;
  @state() hasCTAButtonSlot: boolean;

  firstUpdated(): void {
    super.firstUpdated();
    this.hasBreadcrumbSlot =
      this.querySelector('[slot="hero-banner-breadcrumb"]') !== null;
    this.hasEyebrowSlot =
      this.querySelector('[slot="hero-banner-eyebrow"]') !== null;
    this.hasTitleSlot =
      this.querySelector('[slot="hero-banner-title"]') !== null ||
      this.querySelector('[slot="hero-banner-title-button"]') !== null;

    this.hasAuthorSlot =
      this.querySelector('[slot="hero-banner-author"]') !== null;
    this.hasDateSlot = this.querySelector('[slot="hero-banner-date"]') !== null;
    this.hasLocationSlot =
      this.querySelector('[slot="hero-banner-location"]') !== null;
    this.hasBodyTextSlot =
      this.querySelector('[slot="hero-banner-body-text"]') !== null;
    this.hasButtonsSlot =
      this.querySelector('[slot="hero-banner-buttons"]') !== null;
    this.hasPhoneSlot =
      this.querySelector('[slot="hero-banner-phone-number"]') !== null;
    this.hasSocialSlot =
      this.querySelector('[slot="hero-banner-social-icons"]') !== null;
    this.hasImageSlot =
      this.querySelector('[slot="hero-banner-hero-image"]') !== null;
    this.hasDetailsSlot =
      this.querySelector('[slot="hero-banner-details"]') !== null;
    this.hasFormSlot = this.querySelector('[slot="hero-banner-form"]') !== null;
    this.hasCTAButtonSlot =
      this.querySelector('[slot="hero-banner-cta-button"]') !== null;
  }
  render(): TemplateResult {
    const isMobile: boolean =
      window.innerWidth < (screenSizeMap.get('lg') as number);
    // Set the bellin basic interior hero, rendering without an image.
    // Also handles logic for mobile rendering without an image.
    const blockImageRender: boolean =
      this.site == 'BH' &&
      (!this.hasImageSlot || (this.hideMobileImage && isMobile));

    const bellinFullbleed =
      this.site === 'BH' && this.fullWidth === true && !blockImageRender;

    const classes = {
      'hero-banner': true,
      'hero-banner--form': this.hasFormSlot,
      'hero-banner--details': this.hasDetailsSlot,
      'connect-cta': this.connectCTA,
      'full-width': this.fullWidth,
      'only-title': this.onlyTitle,
      'basic-interior-hero': blockImageRender,
      [this.alignment]: true,
      'hero-banner-form-full-bleed': this.formFullBleed,
    };

    const bgImageStyle = this.backgroundImageUrl
      ? `background-image: url(${this.backgroundImageUrl});`
      : '';

    const sectionClasses = {
      'hero-banner-body-section': true,
      'margin-right': this.hasImageSlot,
    };

    const formClasses = {
      'hero-banner-form-wrapper': true,
      'hero-banner-form-full-bleed': this.formFullBleed,
      'shadow-lg': !this.hasCTAButtonSlot && !this.formFullBleed,
    };

    const heroImageClasses = {
      'hidden': blockImageRender,
      'hero-banner-hero-image': true,
      'alternate': true,
    };

    return html` <div class="${classMap(classes)}" style="${bgImageStyle}">
      <div
        class="hero-banner-background ${this.backgroundVariant} ${this.alternate
          ? 'alternate'
          : ''}"
      >
        <outline-container
          ?x-padding="${!bellinFullbleed}"
          ?full-bleed="${bellinFullbleed}"
        >
          ${this.hasBreadcrumbSlot
            ? html`
                <div class="hero-banner-breadcrumb">
                  <slot name="hero-banner-breadcrumb"></slot>
                </div>
              `
            : nothing}
          ${this.hasEyebrowSlot ||
          this.hasTitleSlot ||
          this.hasAuthorSlot ||
          this.hasDateSlot ||
          this.hasLocationSlot ||
          this.hasBodyTextSlot ||
          this.hasButtonsSlot ||
          this.hasPhoneSlot ||
          this.hasSocialSlot ||
          this.hasImageSlot
            ? html`
                <div class="hero-banner-content-section">
                  <div class="${classMap(sectionClasses)}">
                    ${this.hasEyebrowSlot
                      ? html`
                          <div class="hero-banner-eyebrow">
                            <slot name="hero-banner-eyebrow"></slot>
                          </div>
                        `
                      : nothing}

                    <div class="hero-banner-title">
                      <slot name="hero-banner-title"></slot>
                      <slot name="hero-banner-title-button"></slot>
                    </div>

                    ${this.hasAuthorSlot
                      ? html`
                          <div class="hero-banner-author">
                            <slot name="hero-banner-author"></slot>
                          </div>
                        `
                      : nothing}
                    ${this.hasDateSlot
                      ? html`
                          <div class="hero-banner-date">
                            <ghs-icon
                              name="calendar"
                              size="20px"
                              mobile-size="18px"
                              site=${this.site}
                            ></ghs-icon>

                            <slot name="hero-banner-date"></slot>
                          </div>
                        `
                      : nothing}
                    ${this.hasLocationSlot
                      ? html`
                          <div class="hero-banner-location">
                            <ghs-icon
                              name="pin"
                              size="20px"
                              mobile-size="18px"
                              site=${this.site}
                            ></ghs-icon>

                            <slot name="hero-banner-location"></slot>
                          </div>
                        `
                      : nothing}
                    ${this.hasBodyTextSlot
                      ? html`
                          <div class="hero-banner-body-text">
                            <slot name="hero-banner-body-text"></slot>
                          </div>
                        `
                      : nothing}
                    ${this.hasButtonsSlot
                      ? html`
                          <div class="hero-banner-buttons">
                            <slot name="hero-banner-buttons"></slot>
                          </div>
                        `
                      : nothing}
                    ${this.hasPhoneSlot
                      ? html`
                          <div class="hero-banner-phone-number">
                            <ghs-icon
                              name="phone"
                              size="20px"
                              mobile-size="18px"
                              site=${this.site}
                            ></ghs-icon>

                            <slot name="hero-banner-phone-number"></slot>
                          </div>
                        `
                      : nothing}
                    ${this.hasSocialSlot
                      ? html`
                          <div class="hero-banner-social-icons">
                            <slot name="hero-banner-social-icons"></slot>
                          </div>
                        `
                      : nothing}
                  </div>
                  ${this.hasImageSlot
                    ? html`
                        <div class="${classMap(heroImageClasses)}">
                          <slot name="hero-banner-hero-image"></slot>
                        </div>
                      `
                    : nothing}
                </div>
              `
            : nothing}
        </outline-container>
      </div>
      <div class="hero-banner-content">
        ${this.hasDetailsSlot
          ? html`
              <outline-container x-padding>
                <div class="hero-banner-details-wrapper">
                  <slot name="hero-banner-details"></slot>
                </div>
              </outline-container>
            `
          : nothing}
        ${this.hasFormSlot
          ? html`
              <outline-container
                ?x-padding=${!this.formFullBleed}
                ?full-bleed=${this.formFullBleed}
                ?full-bleed-extend="${this.fullBleedExtend}"
              >
                <div class="${classMap(formClasses)}">
                  <slot name="hero-banner-form"></slot>
                </div>
              </outline-container>
            `
          : nothing}
        ${this.hasCTAButtonSlot
          ? html`
              <outline-container
                ?x-padding=${!this.formFullBleed}
                ?full-bleed=${this.formFullBleed}
              >
                <div>
                  <slot name="hero-banner-cta-button"></slot>
                </div>
              </outline-container>
            `
          : nothing}
      </div>
    </div>`;
  }
}
